import React, { useState, useEffect, useRef } from 'react';
import Spacer from '~/components/Spacer/Spacer';
import { saveProfileNote, getProfileNotes } from '~/api/notes';
import { useSelector } from 'react-redux';
import { RootState } from '~/redux/store';
import { Roles } from '~/enums/Roles';

import './Notes.css';

interface Note {
  uuid: string;
  who: string;
  who_name: string;
  note: string;
  createdAt: string;
}

interface NotesProps {
  type: 'profile' | 'building';
  entityId: string;
}

const Notes: React.FC<NotesProps> = ({ type, entityId }) => {
  // Get user ID from Redux state
  const userId = useSelector((state: RootState) => state.auth.user.uuid);
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const { userRole } = useSelector((state: RootState) => state.auth);
  const isMounted = useRef(true);

  useEffect(() => {
    // Set isMounted to true when component mounts
    isMounted.current = true;
    
    // Fetch notes
    fetchNotes();
    
    // Cleanup function to run when component unmounts
    return () => {
      isMounted.current = false;
    };
  }, [entityId, type]);

  const fetchNotes = async () => {
    if (!isMounted.current) return;
    
    setLoading(true);
    try {
      const response = await getProfileNotes(entityId);
      
      // Only update state if component is still mounted
      if (isMounted.current) {
        setNotes(response.data.rows);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !isMounted.current) return;
    
    try {
      if (type === 'profile') {
        // Use the saveProfileNote function for profile notes
        await saveProfileNote(entityId, newNote, userId);
      } else {
        // For building notes, use alert until saveBuildingNote is implemented
        alert(`Adding building note for ${entityId}: ${newNote}`);
      }
      
      // Only update state if component is still mounted
      if (isMounted.current) {
        // Clear the textarea after successful save
        setNewNote('');
        // Refresh notes list
        fetchNotes();
      }
    } catch (error) {
      console.error('Error saving note:', error);
      if (isMounted.current) {
        alert('Failed to save note');
      }
    }
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="notes-container">
      <p className="small-text bold">NOTES</p>
      <Spacer height={10} />
        {userRole === Roles.Superadmin && (
        <>
          {loading ? (
            <p className="small-text">Loading notes...</p>
          ) : notes.length === 0 ? (
            <p className="small-text">No notes yet.</p>
          ) : (
            <div className="notes-list">
              {notes.map((note) => (
                <div key={note.uuid} className="note-item">
                  <div className="note-header">
                    <span className="small-text bold">{note.who_name}</span>
                    <span className="small-text">{formatDate(note.createdAt)}</span>
                  </div>
                  <Spacer height={5} />
                  <div className="small-text">{note.note}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <Spacer height={10} />
      <div className="add-note">
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Write a note..."
          rows={4}
          className="note-textarea"
        />
        <Spacer height={10} />
        <button 
          className="btn-standard btn-primary cursor-pointer"
          onClick={handleAddNote}
          disabled={!newNote.trim()}
        >
          Add Note
        </button>
      </div>
    </div>
  );
};

export default Notes; 